import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShieldCheck,
    CheckCircle2,
    XCircle,
    Clock,
    ArrowRight,
    Smartphone,
    Receipt,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatCRC } from "../lib/api";

export default function Payment() {
    const { sellerId } = useParams();
    const [seller, setSeller] = useState(null);
    const [loadingSeller, setLoadingSeller] = useState(true);
    const [amount, setAmount] = useState("");
    const [reference, setReference] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/users/${sellerId}/public`);
                setSeller(data);
            } catch (e) {
                setSeller(null);
            } finally {
                setLoadingSeller(false);
            }
        })();
    }, [sellerId]);

    const onSubmit = async (e) => {
        e.preventDefault();
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) {
            toast.error("Ingresá un monto válido");
            return;
        }
        if (!reference.trim() || reference.trim().length < 3) {
            toast.error("Ingresá una referencia válida");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/transactions/verify", {
                seller_id: sellerId,
                amount: amt,
                reference: reference.trim(),
                customer_name: customerName.trim() || null,
            });
            setResult(data);
            if (data.status === "verified") toast.success("Pago verificado");
            else if (data.status === "failed") toast.error("Pago rechazado");
            else toast.info("Pago pendiente");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            // Backend returns structured error with code when seller's basic plan reached limit
            if (detail && typeof detail === "object" && detail.code === "basic_limit_reached") {
                toast.error("Este vendedor llegó a su límite mensual. Contactalo directamente.");
            } else {
                toast.error((typeof detail === "string" ? detail : detail?.message) || "Error al verificar el pago");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setResult(null);
        setAmount("");
        setReference("");
        setCustomerName("");
    };

    if (loadingSeller) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="payment-loading">
                <div className="h-10 w-10 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
            </div>
        );
    }

    if (!seller) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6" data-testid="payment-not-found">
                <div className="card-clean p-10 max-w-md text-center">
                    <div className="h-14 w-14 mx-auto rounded-full bg-[#FEE2E2] flex items-center justify-center">
                        <XCircle className="w-7 h-7 text-[#991B1B]" />
                    </div>
                    <h2 className="font-display text-2xl font-semibold text-[#022C22] mt-5">Link inválido</h2>
                    <p className="text-[#334155] mt-2">
                        Este link de pago no existe o fue desactivado por el vendedor.
                    </p>
                    <Link to="/" className="btn-secondary mt-6 inline-flex">
                        Volver al inicio
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] relative overflow-hidden">
            <div className="absolute inset-0 -z-10">
                <div className="absolute -top-32 right-0 h-[400px] w-[400px] rounded-full bg-[#D1FAE5] opacity-40 blur-3xl" />
                <div className="absolute bottom-0 -left-32 h-[400px] w-[400px] rounded-full bg-[#10B981]/10 blur-3xl" />
            </div>

            <div className="max-w-md md:max-w-lg mx-auto px-5 md:px-6 py-10 md:py-16">
                {/* Brand */}
                <div className="flex items-center justify-center gap-2.5 mb-10" data-testid="payment-brand">
                    <div className="h-10 w-10 rounded-xl bg-[#064E3B] flex items-center justify-center shadow-[0_4px_12px_rgba(6,78,59,0.25)]">
                        <ShieldCheck className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className="font-display font-semibold text-[#022C22] text-base">SINPE-Flow</span>
                        <span className="text-[10px] uppercase tracking-widest text-[#64748B]">Pagos verificados</span>
                    </div>
                </div>

                <AnimatePresence mode="wait">
                    {!result ? (
                        <motion.div
                            key="form"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="card-clean p-7 md:p-9"
                            data-testid="payment-card"
                        >
                            <p className="text-xs uppercase tracking-widest text-[#10B981] font-medium">Pagar a</p>
                            <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#022C22] tracking-tight mt-2 leading-tight" data-testid="seller-name">
                                {seller.business_name || seller.name}
                            </h1>
                            {seller.business_name && (
                                <p className="text-sm text-[#64748B] mt-1.5" data-testid="seller-owner">
                                    Cuenta de <span className="font-medium text-[#334155]">{seller.name}</span>
                                </p>
                            )}
                            {seller.phone_number && (
                                <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-[#F0FDF4] border border-[#D1FAE5]">
                                    <Smartphone className="w-4 h-4 text-[#065F46]" />
                                    <span className="text-sm text-[#065F46]">
                                        SINPE Móvil: <span className="font-mono font-medium" data-testid="seller-phone">{seller.phone_number}</span>
                                    </span>
                                </div>
                            )}

                            <form onSubmit={onSubmit} className="mt-7 space-y-5" data-testid="payment-form">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-[#022C22]">Monto pagado (₡)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="0.00"
                                        className="input-clean font-mono text-lg"
                                        data-testid="amount-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-[#022C22]">Número de referencia bancaria</label>
                                    <input
                                        value={reference}
                                        onChange={(e) => setReference(e.target.value)}
                                        placeholder="Ej. 928374651"
                                        className="input-clean font-mono"
                                        data-testid="reference-input"
                                    />
                                    <p className="text-xs text-[#64748B]">
                                        Lo encontrás en el comprobante de tu transferencia SINPE.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-[#022C22]">Tu nombre (opcional)</label>
                                    <input
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        placeholder="María Pérez"
                                        className="input-clean"
                                        data-testid="customer-name-input"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary w-full justify-center"
                                    data-testid="verify-payment-button"
                                >
                                    {submitting ? (
                                        <>
                                            <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                            Verificando...
                                        </>
                                    ) : (
                                        <>
                                            <Receipt className="w-4 h-4" />
                                            Verificar pago
                                        </>
                                    )}
                                </button>

                                <p className="text-xs text-center text-[#64748B] pt-2">
                                    Tu pago se valida en tiempo real con CyberSINPE.
                                </p>
                            </form>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="result"
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="card-clean p-8 md:p-10 text-center"
                            data-testid="payment-result"
                        >
                            {result.status === "verified" && (
                                <>
                                    <div className="h-16 w-16 mx-auto rounded-full bg-[#D1FAE5] flex items-center justify-center" data-testid="result-verified">
                                        <CheckCircle2 className="w-8 h-8 text-[#065F46]" strokeWidth={2} />
                                    </div>
                                    <h2 className="font-display text-3xl font-semibold text-[#022C22] mt-5">¡Pago verificado!</h2>
                                    <p className="text-[#334155] mt-2">
                                        El vendedor recibió la confirmación al instante.
                                    </p>
                                </>
                            )}
                            {result.status === "failed" && (
                                <>
                                    <div className="h-16 w-16 mx-auto rounded-full bg-[#FEE2E2] flex items-center justify-center" data-testid="result-failed">
                                        <XCircle className="w-8 h-8 text-[#991B1B]" />
                                    </div>
                                    <h2 className="font-display text-3xl font-semibold text-[#022C22] mt-5">Pago rechazado</h2>
                                    <p className="text-[#334155] mt-2">{result.message}</p>
                                </>
                            )}
                            {result.status === "pending" && (
                                <>
                                    <div className="h-16 w-16 mx-auto rounded-full bg-[#FEF3C7] flex items-center justify-center" data-testid="result-pending">
                                        <Clock className="w-8 h-8 text-[#92400E]" />
                                    </div>
                                    <h2 className="font-display text-3xl font-semibold text-[#022C22] mt-5">Pago pendiente</h2>
                                    <p className="text-[#334155] mt-2">{result.message}</p>
                                </>
                            )}

                            <div className="mt-7 grid grid-cols-2 gap-3 text-left">
                                <div className="rounded-xl bg-[#F8FAFC] p-4">
                                    <p className="text-xs uppercase tracking-widest text-[#64748B]">Monto</p>
                                    <p className="font-display text-lg font-semibold text-[#022C22] mt-1">{formatCRC(result.amount)}</p>
                                </div>
                                <div className="rounded-xl bg-[#F8FAFC] p-4">
                                    <p className="text-xs uppercase tracking-widest text-[#64748B]">Referencia</p>
                                    <p className="font-mono text-sm text-[#022C22] mt-1 truncate">{result.reference}</p>
                                </div>
                            </div>

                            <button onClick={resetForm} className="btn-secondary mt-7 mx-auto" data-testid="new-payment-button">
                                Hacer otro pago <ArrowRight className="w-4 h-4" />
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                <p className="text-center text-xs text-[#64748B] mt-8">
                    Protegido por SINPE-Flow · <Link to="/" className="underline hover:text-[#064E3B]">Conocer más</Link>
                </p>
            </div>
        </div>
    );
}
